/**
 * Tenant map — email domain → Cognito User Pool config.
 *
 * These are NOT secrets. Cognito User Pool IDs and Client IDs are public SPA
 * identifiers (visible in every Cognito network request). Safe to commit.
 *
 * After `terraform apply` in infrastructure/aws/envs/dev, copy the output values here:
 *   terraform output cognito_user_pool_id
 *   terraform output cognito_user_pool_client_id
 *
 * To add a new tenant: add one entry below and redeploy the frontend.
 * No DNS changes, no new pipelines, no new subdomains needed.
 */

export const COGNITO_REGION = 'us-east-1';

export const TENANT_MAP = {
  // NexoNurse internal / demo accounts
  // Dev  User Pool: nexonurse-dev-nexonurse  (account 285355564078)
  // Prod User Pool: nexonurse-prod-nexonurse (account 951532862659)
  // REACT_APP_COGNITO_USER_POOL_ID / REACT_APP_COGNITO_CLIENT_ID injected at build time by CI
  'nexonurse.com': {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_NxECVyxV7',
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '63pk4jv7lfll17ecob5gssinfv',
    tenantId: 'nexonurse',
  },

  // Local development — maps localhost to the dev pool
  localhost: {
    userPoolId: 'us-east-1_NxECVyxV7',
    clientId: '63pk4jv7lfll17ecob5gssinfv',
    tenantId: 'nexonurse',
  },

  // Future tenants — uncomment and populate when onboarding new clients:
  // 'fcs.org': {
  //   userPoolId: 'us-east-1_XXXXXXXXX',
  //   clientId: 'XXXXXXXXXXXXXXXXXXXXXXXXXX',
  //   tenantId: 'fcs',
  // },
};

/**
 * Resolve the tenant config for a given email address.
 * Extracts the domain part and looks it up in TENANT_MAP.
 *
 * @param {string} email - Full email address (e.g. "sarah@nexonurse.com")
 * @returns {{ userPoolId, clientId, tenantId } | null}
 */
export function getTenantForEmail(email) {
  if (!email || !email.includes('@')) return null;
  const domain = email.split('@')[1].toLowerCase().trim();
  return TENANT_MAP[domain] || null;
}

/**
 * Resolve tenant config for the current hostname (used on app load).
 * Allows localhost to work during development without entering an email first.
 *
 * @returns {{ userPoolId, clientId, tenantId } | null}
 */
export function getTenantForHostname() {
  const hostname = window.location.hostname;

  // Direct match (localhost, 127.0.0.1)
  if (TENANT_MAP[hostname]) {
    return TENANT_MAP[hostname];
  }

  // Subdomain match: dev.nexonurse.com → nexonurse.com
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const parentDomain = parts.slice(1).join('.');
    if (TENANT_MAP[parentDomain]) {
      return TENANT_MAP[parentDomain];
    }
  }

  return null;
}
